// @systeme: contrat
// Le rétro-planning : une date-pivot (l'anniversaire, la rentrée, le départ) engendre
// des étapes datées à rebours (« J-15 : choisir le cadeau »). Les étapes sont des
// éléments comme les autres : éditables, reportables, supprimables. Toutes les dates
// sont posées par le serveur (règle R2) ; le client n'envoie que des jours choisis.
import { z } from 'zod';
import { SchemaAvisJalon, SchemaCategorieElement } from '../enumerations.js';
import { SchemaElement, SchemaHeure, SchemaInstant, SchemaJour } from './elements.js';
/** Un décalage en jours avant la date-pivot : 0 = le jour même, -15 = quinze jours avant. Jamais après. */
export const SchemaDecalageJours = z.number().int().min(-365).max(0);
export const SchemaModeleSousTache = z.object({
    id: z.uuid(),
    ordre: z.number().int(),
    titre: z.string(),
    detail: z.string().nullable(),
    decalageJours: SchemaDecalageJours,
    categorie: SchemaCategorieElement.nullable(),
});
/** Un modèle livré (anniversaire, rentrée, impôts, vacances, rendez-vous médical, déménagement). */
export const SchemaModeleRetroplanning = z.object({
    id: z.uuid(),
    cle: z.string(),
    nom: z.string(),
    description: z.string(),
    /** La catégorie de la date-pivot et, par défaut, de ses étapes (elle décide des rappels). */
    categorie: SchemaCategorieElement.nullable(),
    sousTaches: z.array(SchemaModeleSousTache),
});
/** Une étape d'un rétro-planning : l'élément lui-même, et ce que le moteur en sait. */
export const SchemaJalon = z.object({
    id: z.uuid(),
    element: SchemaElement,
    decalageJours: SchemaDecalageJours,
    /** La date posée par le moteur au dernier calcul ; différente de celle de l'élément si l'utilisatrice l'a bougée. */
    datePrevueLe: SchemaInstant,
    /** L'utilisatrice a déplacé cette étape elle-même : la propagation la respecte. */
    deplaceALaMain: z.boolean(),
    avis: SchemaAvisJalon.nullable(),
});
export const SchemaRetroplanning = z.object({
    id: z.uuid(),
    espaceId: z.uuid(),
    pivot: SchemaElement,
    modeleCle: z.string().nullable(),
    modeleNom: z.string().nullable(),
    /** La date-pivot telle que le moteur l'a vue au dernier calcul. */
    datePivotLe: SchemaInstant.nullable(),
    /** La date-pivot a bougé depuis : proposer de décaler les étapes (jamais en silence). */
    aDecaler: z.boolean(),
    jalons: z.array(SchemaJalon),
    creeLe: SchemaInstant,
});
/**
 * Créer un rétro-planning : depuis un élément existant (elementId) ou en créant la
 * date-pivot (titre + jour). Un modèle est obligatoire ; « libre » démarre sans étape.
 */
export const SchemaCreationRetroplanning = z
    .object({
    modeleCle: z.string().min(1),
    elementId: z.uuid().optional(),
    titre: z.string().trim().min(1, { error: 'Il manque un titre.' }).max(200).optional(),
    jour: SchemaJour.optional(),
    heure: SchemaHeure.nullable().optional(),
    personneId: z.uuid().nullable().optional(),
})
    .refine((v) => v.elementId !== undefined || (v.titre !== undefined && v.jour !== undefined), {
    error: 'Il faut un titre et une date, ou un élément existant.',
    path: ['jour'],
});
/** Avant de créer : « voici les étapes que je prévois, avec leurs dates ». */
export const SchemaDemandeApercu = z.object({
    modeleCle: z.string().min(1),
    jour: SchemaJour,
});
export const SchemaApercuRetroplanning = z.object({
    modeleCle: z.string(),
    jour: SchemaJour,
    etapes: z.array(z.object({
        titre: z.string(),
        decalageJours: SchemaDecalageJours,
        /** Le jour posé par le serveur, jamais avant aujourd'hui ni après la date-pivot. */
        jour: SchemaJour,
        /** Vrai si l'étape aurait dû être faite avant aujourd'hui : elle est ramenée à aujourd'hui. */
        ramenee: z.boolean(),
    })),
});
export const SchemaAjoutJalon = z.object({
    titre: z.string().trim().min(1, { error: 'Il manque un titre.' }).max(200),
    decalageJours: SchemaDecalageJours,
    categorie: SchemaCategorieElement.nullable().optional(),
});
export const SchemaAvisSurJalon = z.object({ avis: SchemaAvisJalon });
//# sourceMappingURL=retroplanning.js.map